"use client"

import { useState, useRef, useEffect } from "react"
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, SkipBack, SkipForward } from "lucide-react"
import PropTypes from "prop-types"

export default function VideoPlayer({
  thumbnailSrc,
  videoSrc,
  duration: fallbackDuration = "0:00",
  views = "+1M Views",
  alt = "Tutoriel de démonstration - utilisation de Events scan",
  skipTime = 5, // Temps en secondes pour avancer/reculer
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState("0:00")
  const [videoDuration, setVideoDuration] = useState(fallbackDuration)
  const [showControls, setShowControls] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(0)
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const progressBarRef = useRef(null)

  // Fonction pour formater le temps en minutes:secondes
  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds || isNaN(timeInSeconds)) return "0:00"
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  // Gérer la lecture/pause de la vidéo
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
        setHasStartedPlaying(true)
      }
      setIsPlaying(!isPlaying)
      updateLastActivity()
    }
  }

  // Gérer le son
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
      updateLastActivity()
    }
  }

  // Avancer dans la vidéo
  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(videoRef.current.currentTime + skipTime, videoRef.current.duration)
      updateLastActivity()
    }
  }

  // Reculer dans la vidéo
  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - skipTime, 0)
      updateLastActivity()
    }
  }

  // Naviguer à un point spécifique de la vidéo
  const seekTo = (e) => {
    if (!videoRef.current || !progressBarRef.current) return

    const progressBar = progressBarRef.current
    const rect = progressBar.getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const width = rect.width
    const percentage = offsetX / width

    videoRef.current.currentTime = percentage * videoDurationSeconds
    setProgress(percentage * 100)
    updateLastActivity()
  }

  // Gérer le plein écran
  const toggleFullscreen = () => {
    if (!containerRef.current) return

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen()
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
    updateLastActivity()
  }

  // Mettre à jour le timestamp de la dernière activité
  const updateLastActivity = () => {
    setLastActivity(Date.now())
    setShowControls(true)
  }

  // Détecter les changements de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      )
      setIsFullscreen(isNowFullscreen)
      updateLastActivity()
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange)
    document.addEventListener("msfullscreenchange", handleFullscreenChange)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange)
      document.removeEventListener("msfullscreenchange", handleFullscreenChange)
    }
  }, [])

  // Gérer l'affichage des contrôles basé sur l'activité
  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now()
      const inactiveTime = now - lastActivity

      if (inactiveTime > 3000 && isPlaying) {
        setShowControls(false)
      }
    }

    const inactivityInterval = setInterval(checkInactivity, 1000)

    return () => {
      clearInterval(inactivityInterval)
    }
  }, [lastActivity, isPlaying])

  // Récupérer la durée de la vidéo une fois les métadonnées chargées
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedMetadata = () => {
      const durationInSeconds = video.duration
      setVideoDurationSeconds(durationInSeconds)
      setVideoDuration(formatTime(durationInSeconds))
    }

    video.addEventListener("loadedmetadata", handleLoadedMetadata)

    if (video.readyState >= 2) {
      handleLoadedMetadata()
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
    }
  }, [videoSrc])

  // Mettre à jour le temps actuel et la progression pendant la lecture
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const updateTimeAndProgress = () => {
      const currentTimeInSeconds = video.currentTime
      setCurrentTime(formatTime(currentTimeInSeconds))

      if (video.duration) {
        const progressPercentage = (currentTimeInSeconds / video.duration) * 100
        setProgress(progressPercentage)
      }
    }

    video.addEventListener("timeupdate", updateTimeAndProgress)

    return () => {
      video.removeEventListener("timeupdate", updateTimeAndProgress)
    }
  }, [])

  // Gestionnaire d'événements pour le mouvement de la souris
  const handleMouseMove = () => {
    updateLastActivity()
  }

  // Gestionnaire d'événements pour le clic sur le conteneur
  const handleContainerClick = (e) => {
    // Éviter de déclencher le clic si on clique sur un bouton ou la barre de progression
    if (e.target === containerRef.current || e.target === videoRef.current) {
      togglePlay()
    }
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div
        ref={containerRef}
        className="relative flex items-center justify-center w-full rounded-lg overflow-hidden shadow-xl cursor-pointer"
        style={{ aspectRatio: "16/9" }}
        onMouseMove={handleMouseMove}
        onClick={handleContainerClick}
      >
        {/* Thumbnail (visible uniquement avant le premier démarrage) */}
        {!hasStartedPlaying && (
          <img
            className="absolute inset-0 w-full h-full object-cover"
            src={thumbnailSrc || "/placeholder.svg"}
            alt={alt}
          />
        )}

        {/* Vidéo */}
        <video
          ref={videoRef}
          className={`w-full h-full object-cover ${!hasStartedPlaying ? "opacity-0" : "opacity-100"}`}
          src={videoSrc}
          playsInline
          preload="metadata"
          onEnded={() => setIsPlaying(false)}
        />

        {/* Bouton de lecture/pause (visible selon l'état des contrôles) */}
        {(showControls || !isPlaying) && (
          <button
            className="absolute z-10 transition-transform duration-300 hover:scale-110 focus:outline-none"
            onClick={(e) => {
              e.stopPropagation()
              togglePlay()
            }}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm shadow-lg">
              {isPlaying ? (
                <Pause className="w-6 h-6 text-[#5bcfc5]" />
              ) : (
                <Play className="w-6 h-6 text-[#5bcfc5] ml-1" />
              )}
            </div>
          </button>
        )}

        {/* Overlay d'informations et contrôles (visible selon l'état des contrôles) */}
        {(showControls || !isPlaying) && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300">
            {/* Barre de progression */}
            <div
              ref={progressBarRef}
              className="w-full h-1 bg-white/30 rounded-full mb-3 cursor-pointer"
              onClick={seekTo}
            >
              <div className="h-full bg-[#5bcfc5] rounded-full" style={{ width: `${progress}%` }}></div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Bouton reculer */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    skipBackward()
                  }}
                  className="text-white hover:text-[#5bcfc5] transition-colors"
                  aria-label={`Reculer de ${skipTime} secondes`}
                >
                  <SkipBack className="w-5 h-5" />
                </button>

                <div className="text-sm font-medium text-white">
                  {currentTime} / {videoDuration}
                </div>

                {/* Bouton avancer */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    skipForward()
                  }}
                  className="text-white hover:text-[#5bcfc5] transition-colors"
                  aria-label={`Avancer de ${skipTime} secondes`}
                >
                  <SkipForward className="w-5 h-5" />
                </button>

                <div className="text-sm font-medium text-white">{views}</div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleMute()
                  }}
                  className="text-white hover:text-[#5bcfc5] transition-colors"
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleFullscreen()
                  }}
                  className="text-white hover:text-[#5bcfc5] transition-colors"
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                  {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Définition des PropTypes pour la validation des props
VideoPlayer.propTypes = {
  thumbnailSrc: PropTypes.string.isRequired,
  videoSrc: PropTypes.string.isRequired,
  duration: PropTypes.string,
  views: PropTypes.string,
  alt: PropTypes.string,
  skipTime: PropTypes.number,
}
